import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { Home } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useRecordShellEnabled } from '../components/RecordShell/recordShellFlag';
import { RFI_SECTION_LABELS } from '../components/RecordShell/rfiNav';
import { useRecordBreadcrumbs } from '../components/RecordShell/useRecordBreadcrumbs';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { useRfiResponseWorkspace } from '../pages/RfiResponse/hooks';
import { RfiRecordProvider } from '../pages/RfiResponse/workspace/RfiRecordContext';
import { RESPONSE_DOCUMENT_KEY, useRfiAnswerSections } from '../pages/RfiResponse/workspace/useRfiAnswerSections';
/**
 * `/rfi-response/:id` record layout (FND-19). With `growth_record_shell_v1` on
 * it sets the trail (Home / Growth / <pursuit> / <title> / <section>) and hides
 * the generic body header. Inactive when the flag is off, for `new`, or while
 * the title is loading.
 */ export function RfiRecordLayout() {
    const { id = '' } = useParams();
    const location = useLocation();
    const flagOn = useRecordShellEnabled();
    const enabled = flagOn && id !== 'new';
    const { data } = useRfiResponseWorkspace(enabled ? id : '');
    const title = data?.summary?.title;
    const pursuit = data?.linkedPursuits?.[0];
    const [section = '', sectionId = ''] = location.pathname.slice(`/rfi-response/${id}`.length).replace(/^\//, '').split('/');
    // WRT-05: the last crumb at questions/:sectionId is "Question {n}".
    const pangaeaId = data?.summary?.rfiResponseProjectId ?? '';
    const { sections } = useRfiAnswerSections(enabled && section === 'questions' ? pangaeaId : '');
    const questionN = sectionId ? sections.findIndex((s)=>s.id === sectionId) + 1 : 0;
    const lastCrumb = questionN > 0 ? `Question ${questionN}` : RFI_SECTION_LABELS[section] ?? 'Home';
    const qc = useQueryClient();
    const record = useMemo(()=>{
        const summary = data?.summary;
        if (!enabled || !summary) return null;
        const ensured = qc.getQueryData([
            RESPONSE_DOCUMENT_KEY,
            summary.rfiResponseProjectId,
            ''
        ]);
        return {
            pangaeaId: summary.rfiResponseProjectId,
            zouProjectId: summary.zouProjectId,
            routeId: id,
            title: summary.title,
            dueAt: summary.responseDue?.toDate(),
            status: summary.status,
            responseDocumentId: ensured?.responseDocumentId || undefined
        };
    }, [
        enabled,
        data?.summary,
        id,
        qc
    ]);
    useRecordBreadcrumbs(enabled && title ? [
        {
            label: 'Home',
            href: '/',
            icon: /*#__PURE__*/ _jsx(Home, {
                className: "h-3 w-3"
            })
        },
        {
            label: 'Growth',
            href: '/growth'
        },
        ...pursuit ? [
            {
                label: pursuit.captureName,
                href: `/capture/${pursuit.captureProjectId}`
            }
        ] : [],
        {
            label: title,
            href: `/rfi-response/${id}`
        },
        {
            label: lastCrumb
        }
    ] : null);
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    // Hide the generic header only once the trail exists, so loading or a failed load keeps a header.
    const hideHeader = enabled && !!title;
    useEffect(()=>{
        if (!hideHeader) return;
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        hideHeader,
        setShowHeader
    ]);
    return /*#__PURE__*/ _jsx(RfiRecordProvider, {
        value: record,
        children: /*#__PURE__*/ _jsx(Outlet, {})
    });
}
